import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  FormHelperText,
  Tabs,
  Tab,
} from "@mui/material";
import { OpenInNew, ContentCopy } from "@mui/icons-material";
import axios from "axios";

// Determine API base URL and payment page URL based on current hostname
function getApiBaseUrl(): string {
  const hostname = window.location.hostname;
  if (hostname === 'panel.vangardpay.com') {
    return 'https://api.vangardpay.com';
  }
  if (hostname === 'test-panel.vangardpay.com') {
    return 'https://test.api.vangardpay.com';
  }
  return 'http://localhost:3101';
}

function getPaymentPageUrl(): string {
  const hostname = window.location.hostname;
  if (hostname === 'panel.vangardpay.com') {
    return 'https://pay.vangardpay.com';
  }
  if (hostname === 'test-panel.vangardpay.com') {
    return 'https://test.pay.vangardpay.com';
  }
  return 'http://localhost:8085';
}

interface PaymentSession {
  id: string;
  token: string;
  paymentUrl: string;
  expiresAt: string;
  status: string;
}

interface HttpDetails {
  method: string;
  url: string;
  headers: Record<string, string>;
  body: any;
  response: any;
  statusCode: number;
  curl: string;
}

// VP-TEST: словарь банков-получателей из PaymentMaster (tjBanks.js/uzBanks.js) для
// override выдаваемого реквизита на тест-эндпоинте. id != null -> резолвится в PM
// (кнопки T-Банк-СБП/ВТБ). Можно также вписать slug/имя вручную.
const PM_RECIPIENT_BANKS: { name: string; slug: string; country: "TJ" | "UZ" }[] = [
  { name: "Васл Банк", slug: "vasl", country: "TJ" },
  { name: "Душанбе Сити Банк", slug: "dushanbe-city", country: "TJ" },
  { name: "Спитамен Банк", slug: "spitamen", country: "TJ" },
  { name: "Алиф Банк", slug: "alif", country: "TJ" },
  { name: "Банк Эсхата", slug: "eskhata", country: "TJ" },
  { name: "Амонатбонк", slug: "amonatbonk", country: "TJ" },
  { name: "Банк Арванд", slug: "arvand", country: "TJ" },
  { name: "МДО «МАТИН»", slug: "matin", country: "TJ" },
];

const PaymentPageTest: React.FC = () => {
  const [apiKey, setApiKey] = useState("");
  const [amount, setAmount] = useState("5000");
  // VP-PM: новый контракт - только CROSSBORDER_PHONE (трансгран СБП).
  const [method] = useState("CROSSBORDER_PHONE");
  const [currency, setCurrency] = useState("RUB");
  // VP-SENDERBANK-REQUIRED: senderBank обязателен в /api/payment-sessions.
  // Роль прежнего «не указан» несёт явное значение "picker" (экран выбора банка).
  const [senderBank, setSenderBank] = useState("picker");
  const [ttl, setTtl] = useState("3600");
  const [callbackUrl, setCallbackUrl] = useState("");
  const [successUrl, setSuccessUrl] = useState("");
  const [failUrl, setFailUrl] = useState("");
  const [cancelUrl, setCancelUrl] = useState("");

  // VP-TEST: override выдаваемого получателя (тест-эндпоинт /api/payment-sessions/test).
  // Пусто -> боевой /api/payment-sessions (мок сам подставит TJ-получателя).
  const [recipientBankSlug, setRecipientBankSlug] = useState("");
  const [recipientBankName, setRecipientBankName] = useState("");
  const [recipientCountry, setRecipientCountry] = useState<"TJ" | "UZ">("TJ");
  const [recipientDetailType, setRecipientDetailType] = useState<"phone" | "card">("phone");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientCard, setRecipientCard] = useState("");
  const [recipientFio, setRecipientFio] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<PaymentSession | null>(null);
  const [httpDetails, setHttpDetails] = useState<HttpDetails | null>(null);
  const [detailsTab, setDetailsTab] = useState(0);

  const createSession = async () => {
    if (!apiKey) {
      setError("Введите API ключ мерчанта");
      return;
    }

    setLoading(true);
    setError(null);

    // VP-TEST: если задан override получателя — шлём на ТЕСТ-эндпоинт
    // /api/payment-sessions/test (принимает recipient-поля; на проде 404 по
    // TestSurfaceGuard). Иначе — боевой /api/payment-sessions (мок сам подставит
    // TJ-получателя).
    const hasOverride = Boolean(
      recipientBankSlug || recipientBankName || recipientPhone || recipientCard || recipientFio,
    );
    const effectiveMethod =
      hasOverride && recipientDetailType === "card" ? "CROSSBORDER_CARD" : "CROSSBORDER_PHONE";

    const apiUrl = `${getApiBaseUrl()}/api/payment-sessions${hasOverride ? "/test" : ""}`;
    const requestBody: any = {
      amount: parseInt(amount, 10),
      method: effectiveMethod,
      currency,
      ttl: parseInt(ttl, 10),
    };

    // VP-SENDERBANK-REQUIRED: senderBank обязателен — шлём всегда (значение из
    // селекта, по умолчанию "picker" = экран выбора банка на стороне PM).
    requestBody.senderBank = senderBank;
    if (callbackUrl) requestBody.callbackUrl = callbackUrl;
    if (successUrl) requestBody.successUrl = successUrl;
    if (failUrl) requestBody.failUrl = failUrl;
    if (cancelUrl) requestBody.cancelUrl = cancelUrl;

    // VP-TEST: override выдаваемого получателя (только тест-эндпоинт)
    if (hasOverride) {
      if (recipientBankName) requestBody.recipientBank = recipientBankName;
      if (recipientBankSlug) requestBody.recipientBankSlug = recipientBankSlug;
      requestBody.recipientDetailType = recipientDetailType;
      if (recipientPhone) requestBody.recipientPhone = recipientPhone;
      if (recipientCard) requestBody.recipientCard = recipientCard;
      if (recipientFio) requestBody.recipientFio = recipientFio;
      if (recipientCountry) requestBody.recipientCountry = recipientCountry;
    }

    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    };

    // Генерируем cURL
    const curlCmd = `curl -X POST '${apiUrl}' \\\n  -H 'Content-Type: application/json' \\\n  -H 'X-API-Key: ${apiKey}' \\\n  -d '${JSON.stringify(requestBody, null, 2)}'`;

    try {
      const response = await axios.post(apiUrl, requestBody, {
        headers: requestHeaders,
      });

      setHttpDetails({
        method: "POST",
        url: apiUrl,
        headers: requestHeaders,
        body: requestBody,
        response: response.data,
        statusCode: response.status,
        curl: curlCmd,
      });

      // VP-PM: новый flow возвращает готовый PM-URL в response.paymentUrl -
      // используем его напрямую. Раньше URL конструировался как
      // ${payPage}/pay/${token}, но в новом ответе поля token нет -> получался
      // /pay/undefined. Фолбэк на старую схему оставлен для совместимости.
      const paymentUrl =
        response.data.paymentUrl ||
        (response.data.token ? `${getPaymentPageUrl()}/pay/${response.data.token}` : "");
      setSession({
        id: response.data.sessionId,
        token: response.data.token,
        paymentUrl,
        expiresAt: response.data.expiresAt,
        status: "ACTIVE",
      });

      // Auto-open payment page in new tab
      if (paymentUrl) window.open(paymentUrl, "_blank");
    } catch (err: any) {
      const errData = err.response?.data;
      setHttpDetails({
        method: "POST",
        url: apiUrl,
        headers: requestHeaders,
        body: requestBody,
        response: errData || { message: err.message },
        statusCode: err.response?.status || 0,
        curl: curlCmd,
      });
      setError(
        errData?.message || err.message || "Ошибка создания сессии"
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const openPaymentPage = () => {
    if (session) {
      window.open(session.paymentUrl, "_blank");
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        💳 Payment Page Testing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование hosted payment page (PayGate). Создайте платёжную сессию и
        перейдите на страницу оплаты.
      </Typography>

      <Grid container spacing={3}>
        {/* Create Session Form */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Создать платёжную сессию
            </Typography>

            <TextField
              fullWidth
              label="API Key мерчанта"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              type="password"
              margin="normal"
              helperText="X-API-Key заголовок мерчанта"
            />

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Сумма"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  type="number"
                  helperText="Фиксированная сумма"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Метод оплаты</InputLabel>
                  <Select value={method} label="Метод оплаты" disabled>
                    <MenuItem value="CROSSBORDER_PHONE">
                      Трансграничный СБП (CROSSBORDER_PHONE)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Валюта</InputLabel>
                  <Select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    label="Валюта"
                  >
                    <MenuItem value="RUB">RUB</MenuItem>
                    <MenuItem value="USDT">USDT</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="TTL (сек)"
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  type="number"
                  helperText="Время жизни сессии"
                />
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ mt: 2 }} required>
              <InputLabel>Банк плательщика (обязательно)</InputLabel>
              <Select
                value={senderBank}
                onChange={(e) => setSenderBank(e.target.value)}
                label="Банк плательщика (обязательно)"
              >
                <MenuItem value="picker">
                  <em>Выбор банка на странице (picker)</em>
                </MenuItem>
                <MenuItem value="sber">Сбер (deep-link)</MenuItem>
                <MenuItem value="tbank">Т-Банк (deep-link)</MenuItem>
                <MenuItem value="alfa">Альфа (deep-link)</MenuItem>
                <MenuItem value="vtb">ВТБ (deep-link)</MenuItem>
                <MenuItem value="other">Другой — универсальная страница</MenuItem>
                {/* VP-PM-SETTINGS 2026-07-20: домен открыт полностью. Фактически
                    принимаемый набор — настройка админки; бэкенд отдаст 400, если
                    банк выключен. */}
              </Select>
              <FormHelperText>
                picker — экран выбора банка; конкретный банк — переход в его приложение; other — универсальная страница. Фактически принимаемый набор задаётся в админке (Настройки PaymentMaster).
              </FormHelperText>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Выдаваемый реквизит получателя (тест) — пусто = авто (мок подставит TJ)
            </Typography>

            <FormControl fullWidth sx={{ mt: 1 }} size="small">
              <InputLabel>Банк получателя</InputLabel>
              <Select
                value={recipientBankSlug}
                label="Банк получателя"
                onChange={(e) => {
                  const slug = e.target.value as string;
                  setRecipientBankSlug(slug);
                  const opt = PM_RECIPIENT_BANKS.find((b) => b.slug === slug);
                  if (opt) {
                    setRecipientBankName(opt.name);
                    setRecipientCountry(opt.country);
                  }
                }}
              >
                <MenuItem value="">
                  <em>Авто (мок подставит TJ)</em>
                </MenuItem>
                {PM_RECIPIENT_BANKS.map((b) => (
                  <MenuItem key={b.slug} value={b.slug}>
                    {b.name} ({b.country})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Из словаря PaymentMaster (резолвится в id) либо впишите slug/имя вручную ниже.
              </FormHelperText>
            </FormControl>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="bankSlug (override)"
                  value={recipientBankSlug}
                  onChange={(e) => setRecipientBankSlug(e.target.value)}
                  placeholder="vasl"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Имя банка (опц.)"
                  value={recipientBankName}
                  onChange={(e) => setRecipientBankName(e.target.value)}
                  placeholder="Васл Банк"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Способ выдачи</InputLabel>
                  <Select
                    value={recipientDetailType}
                    label="Способ выдачи"
                    onChange={(e) =>
                      setRecipientDetailType(e.target.value as "phone" | "card")
                    }
                  >
                    <MenuItem value="phone">Номер телефона (СБП)</MenuItem>
                    <MenuItem value="card">C2C (карта)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Страна</InputLabel>
                  <Select
                    value={recipientCountry}
                    label="Страна"
                    onChange={(e) => setRecipientCountry(e.target.value as "TJ" | "UZ")}
                  >
                    <MenuItem value="TJ">TJ</MenuItem>
                    <MenuItem value="UZ">UZ</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {recipientDetailType === "phone" ? (
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Телефон получателя"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="+992700112233"
                helperText="Пусто -> мок сгенерирует +992…"
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Карта получателя (C2C)"
                value={recipientCard}
                onChange={(e) => setRecipientCard(e.target.value)}
                placeholder="4111111111111111"
              />
            )}

            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="ФИО получателя (опц.)"
              value={recipientFio}
              onChange={(e) => setRecipientFio(e.target.value)}
              placeholder="Холов Исломҷон"
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Redirect URLs (опционально)
            </Typography>

            <TextField
              fullWidth
              label="Callback URL"
              value={callbackUrl}
              onChange={(e) => setCallbackUrl(e.target.value)}
              margin="dense"
              size="small"
              placeholder="https://your-site.com/webhook"
            />
            <TextField
              fullWidth
              label="Success URL"
              value={successUrl}
              onChange={(e) => setSuccessUrl(e.target.value)}
              margin="dense"
              size="small"
              placeholder="https://your-site.com/success"
            />
            <TextField
              fullWidth
              label="Fail URL"
              value={failUrl}
              onChange={(e) => setFailUrl(e.target.value)}
              margin="dense"
              size="small"
              placeholder="https://your-site.com/fail"
            />
            <TextField
              fullWidth
              label="Cancel URL"
              value={cancelUrl}
              onChange={(e) => setCancelUrl(e.target.value)}
              margin="dense"
              size="small"
              placeholder="https://your-site.com/cancel"
            />

            <Button
              variant="contained"
              fullWidth
              onClick={createSession}
              disabled={loading}
              sx={{ mt: 3 }}
              size="large"
            >
              {loading ? "Создание..." : "Создать сессию"}
            </Button>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Result */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Результат
            </Typography>

            {session ? (
              <Box>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box
                      sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}
                    >
                      <Chip
                        label={session.status}
                        color={session.status === "ACTIVE" ? "success" : "default"}
                        size="small"
                      />
                      <Chip
                        label={`ID: ${session.id.slice(0, 8)}...`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Typography variant="subtitle2" color="text.secondary">
                      Payment URL
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          flex: 1,
                          color: "primary.main",
                        }}
                      >
                        {session.paymentUrl}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => copyToClipboard(session.paymentUrl)}
                      >
                        Copy
                      </Button>
                    </Box>

                    <Typography variant="subtitle2" color="text.secondary">
                      Истекает
                    </Typography>
                    <Typography variant="body2">
                      {new Date(session.expiresAt).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>

                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  size="large"
                  startIcon={<OpenInNew />}
                  onClick={openPaymentPage}
                >
                  Открыть платёжную страницу
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 200,
                  color: "text.secondary",
                }}
              >
                <Typography variant="h2" sx={{ mb: 2 }}>
                  💳
                </Typography>
                <Typography>
                  Создайте платёжную сессию чтобы получить ссылку для оплаты
                </Typography>
              </Box>
            )}

            {/* HTTP Details */}
            {httpDetails && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  🔍 HTTP Details
                </Typography>

                <Tabs
                  value={detailsTab}
                  onChange={(_, v) => setDetailsTab(v)}
                  sx={{ mb: 2 }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="cURL" />
                  <Tab label="Headers" />
                  <Tab label="Request" />
                  <Tab label="Response" />
                </Tabs>

                {detailsTab === 0 && (
                  <Box sx={{ position: "relative" }}>
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => copyToClipboard(httpDetails.curl)}
                      sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                    >
                      Copy
                    </Button>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "grey.900",
                        color: "grey.100",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        fontSize: 12,
                        maxHeight: 300,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {httpDetails.curl}
                    </Box>
                  </Box>
                )}

                {detailsTab === 1 && (
                  <Box sx={{ position: "relative" }}>
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => copyToClipboard(JSON.stringify(httpDetails.headers, null, 2))}
                      sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                    >
                      Copy
                    </Button>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "grey.900",
                        color: "grey.100",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        fontSize: 12,
                        maxHeight: 300,
                      }}
                    >
                      <code>
                        {`${httpDetails.method} ${httpDetails.url}\n\n`}
                        {Object.entries(httpDetails.headers).map(([k, v]) => `${k}: ${v}\n`).join("")}
                      </code>
                    </Box>
                  </Box>
                )}

                {detailsTab === 2 && (
                  <Box sx={{ position: "relative" }}>
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => copyToClipboard(JSON.stringify(httpDetails.body, null, 2))}
                      sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                    >
                      Copy
                    </Button>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "grey.900",
                        color: "grey.100",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        fontSize: 12,
                        maxHeight: 400,
                      }}
                    >
                      {JSON.stringify(httpDetails.body, null, 2)}
                    </Box>
                  </Box>
                )}

                {detailsTab === 3 && (
                  <Box sx={{ position: "relative" }}>
                    <Chip
                      label={`Status: ${httpDetails.statusCode}`}
                      color={httpDetails.statusCode >= 200 && httpDetails.statusCode < 300 ? "success" : "error"}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => copyToClipboard(JSON.stringify(httpDetails.response, null, 2))}
                      sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                    >
                      Copy
                    </Button>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "grey.900",
                        color: httpDetails.statusCode >= 200 && httpDetails.statusCode < 300 ? "#4caf50" : "#f44336",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        fontSize: 12,
                        maxHeight: 400,
                      }}
                    >
                      {JSON.stringify(httpDetails.response, null, 2)}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Как это работает:</strong>
          <br />
          1. Мерчант создаёт платёжную сессию через API с указанием суммы и метода
          <br />
          2. API создаёт ордер автоматически и возвращает token + URL страницы
          <br />
          3. Мерчант редиректит клиента на эту страницу
          <br />
          4. Клиент видит реквизиты для оплаты сразу (без выбора метода)
          <br />
          5. После оплаты/отмены клиент редиректится на success/fail URL
        </Typography>
      </Alert>
    </Box>
  );
};

export default PaymentPageTest;
